// Module: liveness.js
// Purpose: Pure helpers deriving the ONE user-facing liveness label for a running strategy
//   deployment (runtime-status-live), plus a compact duration formatter shared with the per-node
//   STALE label (E5a, SignalEvaluationPanel.jsx). The label is composed from worker-authored
//   runtime-health facts (the `runtime_health` blob, seeded by REST and refreshed by push) plus
//   the MDMS-fed exchange-status answer (`exchangeOpen`, read once per strategy by AlgoDashboard
//   from exchangeHours.isExchangeOpenNow(instrument_exchange) — the ONLY session source; this
//   module stays import-free of the exchange store so formatAge's other importers keep a pure,
//   axios-free module graph). Nothing here ages a snapshot against the browser clock:
//   "Worker offline" is decided by the per-deployment heartbeat deadline in TradingDataContext
//   (or a graceful-shutdown blob) and arrives as the `runtimeOffline` flag.
//   No React, no fetch and no browser clock at all — the counter-window ("no signals in X")
//   sub-note is measured on the worker's own clock (as_of_ms - counters_since_ms).
//   See docs/pipelines/2026-10-19-issue-dashboard-runtime-status-live/02-fix-design.md.

import { rejectReasonLabel } from '../../../utils/rejectReason';

/*>>> CONSTANTS <<<*/
//! "no signals" must compare against the counter's OWN observation window
//  (`counters_since_ms`), NEVER deployment uptime — comparing against uptime false-alarms on
//  every worker restart. One full trading day is used so a legitimately quiet strategy inside
//  its own session never fires, independent of any one exchange's specific session length.
const ONE_FULL_SESSION_MS = 24 * 60 * 60 * 1000; // 24h

//--> Tone -> colour for the three dashboard surfaces (card, row, mobile). `warning` keeps the
//*   warning colour; `ok` / `muted` are quiet.
const WARNING_COLOR = 'color-mix(in srgb, var(--warning) 55%, var(--fg-1))';
const QUIET_COLOR = 'var(--fg-3)';
export const livenessToneColor = (tone) => (tone === 'warning' ? WARNING_COLOR : QUIET_COLOR);

/*>>> DURATION FORMAT — shared with SignalEvaluationPanel's `STALE <age>` label <<<*/
//  Pure ms-duration -> compact string. NOT a "time ago" formatter (no Date parsing, no
//  "ago" suffix) — callers already hold a millisecond delta.
export function formatAge(ms) {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '';
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

/*>>> runtime_health blob validity — the ONE definition (REST seed, push and label share it) <<<*/
//  A usable blob is an object carrying a worker-authored, finite, positive `stale_after_ms`.
//  An old/malformed blob (pre-rollout worker) is NOT usable: no deadline can be armed from it
//  and the label must never read Live from it.
export function isValidRuntimeHealth(blob) {
  return (
    blob != null
    && typeof blob === 'object'
    && !Array.isArray(blob)
    && Number.isFinite(blob.stale_after_ms)
    && blob.stale_after_ms > 0
  );
}

//--> Secondary warmth detail from the blob's own `warm` / `not_warm` (NOT the primary warm
//*   source) — used only as a sub-note.
function notWarmSubNote(blob) {
  if (blob.warm !== false) return null;
  const item = Array.isArray(blob.not_warm) && blob.not_warm.length > 0 ? blob.not_warm[0] : null;
  const tf = item?.timeframe ? `${item.timeframe} ` : '';
  if (item?.reason === 'stale') return `${tf}input stale — ${formatAge(item.age_ms)} old`;
  if (item) return `not warm — ${tf}${item.bars_observed ?? '?'}/${item.bars_required ?? '?'} bars`;
  return 'not warm';
}

//--> Primary label for the calc_liveness state (row 5/6). `calc_liveness.warm` is the ONLY
//*   warm source for the label; the worker-side session flag is never read (market state comes
//*   only from the exchange-status answer).
function calcLabel(calcLiveness) {
  const state = calcLiveness && typeof calcLiveness === 'object' ? calcLiveness.state : null;
  if (state === 'ok') return { tone: 'ok', text: 'Live' };
  if (state === 'cold' || state === 'undersized') {
    const warm = calcLiveness.warm;
    const observed = warm?.bars_observed;
    const required = warm?.bars_required;
    const text = Number.isFinite(observed) && Number.isFinite(required)
      ? `Warming up ${observed}/${required} bars`
      : 'Warming up';
    return { tone: 'warning', text };
  }
  //! hole / stuck / indeterminate / missing / unknown: never Live (instance_manager.py
  //  _build_calc_liveness — green by omission is forbidden).
  return { tone: 'warning', text: 'Input stale' };
}

/*>>> deriveLivenessNote — the ONLY place any dashboard surface may derive this label <<<*/
//  strategy: the ALREADY-CAMELCASED client object AlgoDashboard.jsx builds — status,
//    runtimeHealth (raw worker blob | null), runtimeOffline (heartbeat deadline fired),
//    exchangeOpen (isExchangeOpenNow(instrument_exchange): true | false | 'UNKNOWN').
//  Returns null (non-running) or { tone: 'warning'|'muted'|'ok', text: string }.
//  Precedence: 1 Worker offline > 2 Waiting for worker > 3 State not saving >
//    4 Waiting for market open (only exchangeOpen === false; UNKNOWN counts as open) >
//    5 Warming up n/m bars | Input stale > 6 Live.
export function deriveLivenessNote(strategy) {
  if (!strategy || strategy.status !== 'running') return null;

  const { runtimeHealth = null, runtimeOffline = false, exchangeOpen = null } = strategy;
  const valid = isValidRuntimeHealth(runtimeHealth);

  //--> 1: graceful-shutdown blob, or the heartbeat deadline fired (any session).
  if (runtimeOffline === true || (valid && runtimeHealth.offline === true)) {
    return { tone: 'warning', text: 'Worker offline' };
  }

  //--> 2: running but no usable blob yet — never Live, never offline.
  if (!valid) return { tone: 'muted', text: 'Waiting for worker' };

  //--> 3: the worker cannot save this strategy's memory (any session). ONLY `false` fires.
  if (runtimeHealth.state_persist_ok === false) {
    return { tone: 'warning', text: 'State not saving' };
  }

  //--> 4/5/6: primary label, then sub-notes.
  //! Only strict `false` means closed — EXCHANGE_STATUS_UNKNOWN (and null) is fail-open
  //  (exchangeHours.js).
  let primary;
  if (exchangeOpen === false) {
    primary = { tone: 'muted', text: 'Waiting for market open' };
  } else {
    primary = calcLabel(runtimeHealth.calc_liveness);
  }

  const subNotes = [];
  const notWarm = notWarmSubNote(runtimeHealth);
  if (notWarm) subNotes.push(notWarm);
  //! The counter window is measured on the WORKER's clock only (its own as_of_ms minus its
  //  own counters_since_ms) — never against the browser clock, so a held healthy card can
  //  never change text just because browser time passes (F-1, runtime-status-live).
  const finite = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  const sinceMs = finite(runtimeHealth.counters_since_ms);
  const asOfMs = finite(runtimeHealth.as_of_ms);
  const windowMs = sinceMs != null && asOfMs != null ? asOfMs - sinceMs : null;
  //! Running counters come only from the worker blob (D7: no flat copy in the mapper).
  //! signals_generated === null means "nobody is counting" — it must NEVER be read as 0.
  const signalsGenerated = runtimeHealth.signals_generated;
  if (signalsGenerated === 0 && windowMs != null && windowMs > ONE_FULL_SESSION_MS) {
    subNotes.push(`no signals in ${formatAge(windowMs)}`);
  }

  if (subNotes.length === 0) return primary;
  return { tone: 'warning', text: [primary.text, ...subNotes].join(' · ') };
}

/*>>> deriveSessionCoverage — current-session coverage line (144-2 R2/R3) <<<*/
//  The ONE composition of "was this paper deployment live and evaluating this session". Pure: it
//  DERIVES from existing authorities and persists nothing —
//    strategy.sessionCoverageEvidence | strategy.session_coverage_evidence : the additive
//      `session_coverage_evidence` on the deployment response ({session_date, expected_session,
//      evaluated_bars, skip_counts{token:n}, first_bar_at, last_bar_at, evidence_status}); the
//      backend (decision rows + MDMS session answer) owns it, this module never recounts bars.
//    strategy.runtimeHealth / runtimeOffline : worker-authored runtime health (same as the liveness note).
//    strategy.exchangeOpen : MDMS-fed (true | false | 'UNKNOWN') — only strict `false` means closed.
//    opts.refusalCounts : { reject_reason_token: count } of THIS session's OMS refusals of the
//      deployment, counted by the caller from the OMS rows it already holds.
//  Returns null (non-running) or
//    { status: 'covered'|'degraded'|'absent'|'coverage_unknown'|'closed', cause: string|null,
//      causes: string[], tone, text, evaluatedBars: number|null,
//      reasonCounts: [{ reason, count, source: 'runner'|'oms', label }] }
//  `reason` / `cause` tokens come from the shared taxonomy (entry_outcome_reasons.py); every text
//  is rendered by rejectReasonLabel — the single humanizer.
//  Precedence: authority unavailable -> coverage_unknown; MDMS no-session -> closed (explicit);
//    worker absent -> absent; feed stale / skips / refusals -> degraded(cause); expected session
//    + evaluated rows with a recent last bar -> covered (stale last bar -> degraded(stale_evidence));
//    expected session + zero rows -> coverage_unknown
//    (absence of evidence is NEVER proof of a strategy divergence or of coverage).
const UNAVAILABLE_EVIDENCE_STATUSES = new Set(['unavailable', 'unknown', 'coverage_unknown', 'error']);
const NO_SESSION_EVIDENCE_STATUSES = new Set(['holiday', 'closed', 'no_session']);

function positiveCounts(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];
  return Object.entries(obj)
    .filter(([, n]) => typeof n === 'number' && Number.isFinite(n) && n > 0)
    .map(([reason, count]) => ({ reason, count }));
}

//--> Feed stale = the worker's own calc_liveness says the input is not ok/warming (same
//*   predicate as calcLabel's "Input stale"), observed while the market is not known closed.
function feedStale(runtimeHealth, exchangeOpen) {
  if (exchangeOpen === false || !isValidRuntimeHealth(runtimeHealth)) return false;
  const state = runtimeHealth.calc_liveness && typeof runtimeHealth.calc_liveness === 'object'
    ? runtimeHealth.calc_liveness.state
    : null;
  if (state == null) return false; // no calc facts -> no claim (never guess a feed fault)
  return !(state === 'ok' || state === 'cold' || state === 'undersized');
}

//--> V1-6: '5m' | '15m' | '1h' -> ms (daily+ bars: no freshness claim, null).
function timeframeMs(tf) {
  const m = /^(\d+)\s*(m|h)$/i.exec(String(tf ?? '').trim());
  if (!m) return null;
  return Number(m[1]) * (m[2].toLowerCase() === 'h' ? 60 : 1) * 60 * 1000;
}

//--> V1-6: is the last evaluated bar within 2x timeframe of the session's last EXPECTED bar?
//*   Reference = min(now, session_end_at); an unknown end with the market known closed -> no claim.
//*   Returns true (fresh/unknown-claim) | false (stale).
function evidenceFresh(strategy, evidence, nowMs) {
  const tfMs = timeframeMs(strategy.timeframe);
  if (tfMs == null) return true;
  const last = Date.parse(evidence.last_bar_at);
  if (!Number.isFinite(last)) return false; // evaluated bars but no usable last_bar_at: cannot prove recency
  const end = Date.parse(evidence.session_end_at);
  let ref;
  if (Number.isFinite(end)) {
    if (!Number.isFinite(nowMs)) return false;
    ref = Math.min(nowMs, end);
  }
  else if (strategy.exchangeOpen === false) return true;
  else {
    if (!Number.isFinite(nowMs)) return false;
    ref = nowMs;
  }
  const lastExpectedBar = Math.floor(ref / tfMs) * tfMs - tfMs;
  return lastExpectedBar - last <= 2 * tfMs;
}

export function deriveSessionCoverage(strategy, opts = {}) {
  if (!strategy || strategy.status !== 'running') return null;
  const { runtimeHealth = null, runtimeOffline = false, exchangeOpen = null } = strategy;
  const evidence = strategy.sessionCoverageEvidence ?? strategy.session_coverage_evidence ?? null;
  const lastBarAt = typeof evidence?.last_bar_at === 'string' ? evidence.last_bar_at : null;

  const make = (status, cause, tone, text, evaluatedBars = null, reasonCounts = [], causes = []) => ({
    status, cause, causes: causes.length ? causes : (cause ? [cause] : []), tone, text, evaluatedBars, reasonCounts, lastBarAt,
  });

  //--> 1: authority unavailable -> coverage_unknown (never read as zero).
  if (
    !evidence || typeof evidence !== 'object' || Array.isArray(evidence)
    || UNAVAILABLE_EVIDENCE_STATUSES.has(evidence.evidence_status)
  ) {
    return make('coverage_unknown', 'evidence_unavailable', 'muted', 'Coverage unknown — session evidence unavailable');
  }

  //--> 2: MDMS says there is no session (holiday / closed) -> explicit, never a silent gap.
  if (evidence.expected_session === false || NO_SESSION_EVIDENCE_STATUSES.has(evidence.evidence_status)) {
    const why = typeof evidence.session_reason === 'string' && evidence.session_reason
      ? evidence.session_reason
      : (evidence.evidence_status === 'holiday' ? 'holiday' : 'no session');
    return make('closed', why, 'muted', `No session today — ${why}`);
  }

  const finiteEvaluated = typeof evidence.evaluated_bars === 'number' && Number.isFinite(evidence.evaluated_bars)
    ? evidence.evaluated_bars
    : null;
  const reasonCounts = [
    ...positiveCounts(evidence.skip_counts).map((c) => ({ ...c, source: 'runner' })),
    ...positiveCounts(opts.refusalCounts).map((c) => ({ ...c, source: 'oms' })),
  ]
    .map((c) => ({ ...c, label: rejectReasonLabel(c.reason) }))
    .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));

  //--> 3: worker absent.
  //  V1-6: a RUNNING deployment with no worker status blob at all is absent, not "coverage unknown".
  if (
    runtimeOffline === true || !isValidRuntimeHealth(runtimeHealth)
    || runtimeHealth.offline === true
  ) {
    return make('absent', 'worker_absent', 'warning', `Absent — ${rejectReasonLabel('worker_absent')}`, finiteEvaluated, reasonCounts);
  }

  //--> 4: degraded(cause) — feed stale > runner skips > entry refusals.
  const causes = [];
  if (feedStale(runtimeHealth, exchangeOpen)) causes.push('feed_stale');
  if (reasonCounts.some((c) => c.source === 'runner')) causes.push('runner_skips_observed');
  if (reasonCounts.some((c) => c.source === 'oms')) causes.push('entry_refusals_observed');
  if (causes.length > 0) {
    return make('degraded', causes[0], 'warning', `Degraded — ${rejectReasonLabel(causes[0])}`, finiteEvaluated, reasonCounts, causes);
  }

  //--> 5: covered needs durable evaluated rows; zero rows is unknown, not covered, not divergence.
  if (finiteEvaluated != null && finiteEvaluated > 0) {
    //  V1-6: covered also needs a RECENT last bar; otherwise degraded(stale_evidence).
    if (!evidenceFresh(strategy, evidence, opts.now)) {
      return make('degraded', 'stale_evidence', 'warning', `Degraded — ${rejectReasonLabel('stale_evidence')}`, finiteEvaluated, reasonCounts);
    }
    return make('covered', null, 'ok', `Covered — ${finiteEvaluated} bars evaluated`, finiteEvaluated, reasonCounts);
  }
  return make('coverage_unknown', 'no_evaluated_bars', 'muted', 'Coverage unknown — no bars evaluated yet', finiteEvaluated, reasonCounts);
}
